import { Method, AxiosResponse } from 'axios';
import { useCallback } from 'react';
import {
    useRequestState,
    requestError,
    requestStart,
    requestSuccess,
} from './useRequestState';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getAxiosClient } from '@givelify/utils';
import { webConfig } from 'webConfig';

export const handleErrorMessages = (e: any, t: TFunction): Error => {
    if (!e.isAxiosError || !e.response) return e;
    const { status } = e.response;
    if (status === 404) {
        e.message = t('error.status.404');
    } else if (status === 500) {
        e.message = t('error.status.500');
    } else if (
        status === 422 &&
        e.response.data?.errors?.officialEmail &&
        e.response.data.errors.officialEmail[0]
    ) {
        e.message = e.response.data.errors.officialEmail[0];
    } else if (e.response.data?.message) {
        e.message = e.response.data.message;
    } else {
        e.message = t('error.status.serverError');
    }
    return e;
};

/**
 * Hook returns a function that can be used to make requests at a later time
 * (Such as in response to user interactions)
 *
 * @param {string} url - endpoint for the request
 * @param {Method} method - Http request method
 * @param {T} data
 */

export const useInvokeApi = <
    Response,
    Request = unknown,
    ErrorResponse = unknown,
>(
    headers?: object,
) => {
    const { t } = useTranslation();
    const [requestState, setRequestState] = useRequestState<
        Response,
        ErrorResponse
    >();
    const makeRequest = useCallback(
        (method: Method, url: string, data?: Request) => {
            const axiosClient = getAxiosClient({ baseURL: webConfig.baseUrl });
            setRequestState(requestStart());
            axiosClient(url, {
                method,
                data,
                headers,
            })
                .then((result: AxiosResponse<any>) => {
                    setRequestState(requestSuccess(result.data));
                })
                .catch((e: Error) => {
                    setRequestState(requestError(handleErrorMessages(e, t)));
                });
        },
        [setRequestState, t, headers],
    );
    return [requestState, makeRequest] as const;
};
